import { useState } from "react";
import "bootstrap-icons/font/bootstrap-icons.css";
import Swal from "sweetalert2";
import { useTheme } from "../../context/ThemeContext";
import GlobalAdsModal from "../ads/GlobalAdsModal";
import { CLUB_ID, THEME_COLOR } from "../../config/club";

export default function AuthLanding({
  themeColor,
  loggingIn,
  onGuestLogin, // Maintained hook capability if needed elsewhere
  onGoogleLogin,
}) {
  const [accepted, setAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const { darkMode, setDarkMode } = useTheme();

  return (
    <div
      className="min-vh-100 d-flex align-items-center justify-content-center position-relative m-0"
      style={{
        background: "var(--app-bg, #0f172a)",
        backgroundImage: `
          radial-gradient(circle at 50% -20%, ${themeColor}15, transparent 70%),
          radial-gradient(circle at 0% 100%, ${themeColor}08, transparent 40%)
        `,
        padding: "0",
        overflowX: "hidden",
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}
    >

      {/* Premium Ambient Background Elements (Visible heavily on desktop, subtle on mobile) */}
      <div
        className="position-absolute top-0 start-50 translate-middle-x d-none d-sm-block"
        style={{
          width: "600px",
          height: "300px",
          background: `radial-gradient(ellipse at center, ${themeColor}12, transparent 65%)`,
          filter: "blur(40px)",
          zIndex: 0
        }}
      />

      {/* Main Container - App Viewport Mock on Desktop / Full Screen on Mobile */}
      <div
        className="w-100 d-flex flex-column justify-content-between position-relative px-4 py-5"
        style={{
          maxWidth: "440px",
          minHeight: "100vh",
          backgroundColor: "transparent",
          zIndex: 1
        }}
      >
        {/* Floating Theme Controller - Top Right */}
        <div className="d-flex justify-content-end mb-4">
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="btn d-flex align-items-center gap-2 px-3 py-2 border-0"
            style={{
              background: "color-mix(in srgb, var(--card-bg, #1e293b) 40%, transparent)",
              backdropFilter: "blur(12px)",
              color: "var(--text-color, #f8fafc)",
              borderRadius: "100px",
              fontSize: "0.8rem",
              fontWeight: "600",
              letterSpacing: "0.05em",
              border: "1px solid rgba(255,255,255,0.05)",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              transition: "all 0.3s ease"
            }}
          >
            {darkMode ? (
              <>
                <i className="bi bi-sun-fill text-warning"></i>
                <span className="opacity-75">DAY SESSION</span>
              </>
            ) : (
              <>
                <i className="bi bi-moon-stars-fill" style={{ color: themeColor }}></i>
                <span className="opacity-75">NIGHT SESSION</span>
              </>
            )}
          </button>
        </div>

        {/* Brand Hero Identity Section */}
        <div className="text-center my-auto pt-2">
          <div className="position-relative d-inline-block mb-4">
            {/* Soft Behind-Logo Luxury Glow */}
            <div
              className="position-absolute top-50 start-50 translate-middle"
              style={{
                width: "140px",
                height: "140px",
                backgroundColor: themeColor,
                filter: "blur(30px)",
                opacity: "0.25",
                zIndex: 0
              }}
            />

            <img
              src="/img/rrc.png"
              alt="Logo"
              className="position-relative img-fluid"
              style={{
                width: "120px",
                height: "120px",
                borderRadius: "32px",
                objectFit: "cover",
                zIndex: 1,
                border: "1px solid rgba(255,255,255,0.1)",
                boxShadow: "0 20px 40px rgba(0,0,0,0.3)"
              }}
            />
          </div>

          <h1
            className="fw-extrabold tracking-tight mb-2"
            style={{
              color: "var(--text-color, #f8fafc)",
              fontSize: "2.25rem",
              fontWeight: "800",
              letterSpacing: "-0.03em"
            }}
          >
            RRC <span style={{ color: themeColor }}>Pickleball</span>
          </h1>

          <p
            className="mx-auto text-muted mb-5 px-2"
            style={{ fontSize: "0.95rem", lineHeight: "1.5", maxWidth: "320px", opacity: 0.8 }}
          >
            Smarter matchmaking, faster games.<br />Less waiting, more dinking.
          </p>
        </div>
        <img
          src="/img/pickle.png"
          alt="Pickleball Background Accent"
          style={{
            position: 'absolute',
            top: '55%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '250px',
            height: '250px',
            objectFit: 'contain',
            opacity: .4,
            pointerEvents: 'none',
            userSelect: 'none',
            zIndex: 0
          }}
        />
        {/* Core Interactions Frame */}
        <div className="w-100 mt-auto">
          {/* Subtle Dynamic Status Label */}
          <div className="d-flex align-items-center justify-content-center gap-3 mb-4">

            <div className="flex-grow-1" style={{ height: "1px", background: "linear-gradient(to right, transparent, rgba(255,255,255,0.08))" }} />
            <span
              className="text-uppercase tracking-widest text-muted fw-bold"
              style={{ fontSize: "0.65rem", letterSpacing: "0.25em", opacity: 0.6 }}
            >
              Zero - Zero - Start
            </span>
            <div className="flex-grow-1" style={{ height: "1px", background: "linear-gradient(to left, transparent, rgba(255,255,255,0.08))" }} />
          </div>

          {/* Premium Google Auth Button */}
          <button
            className="btn w-100 d-flex align-items-center justify-content-center gap-3 mb-4 premium-auth-btn"
            style={{
              height: "56px",
              borderRadius: "16px",
              backgroundColor: "var(--card-bg, #1e293b)",
              color: "var(--text-color, #f8fafc)",
              fontSize: "1rem",
              fontWeight: "600",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
              transition: "all 0.25s cubic-bezier(0.16, 1, 0.3, 1)"
            }}
            disabled={loggingIn}
            onClick={() => {
              if (!accepted) {
                Swal.fire({
                  icon: "warning",
                  title: "Stay out of the Kitchen!",
                  text: "Please agree to the Court Rules before serving your authentication.",
                  confirmButtonColor: themeColor,
                  background: "var(--card-bg, #1e293b)",
                  color: "var(--text-color, #f8fafc)"
                });
                return;
              }
              onGoogleLogin();
            }}
          >
            <i className="bi bi-google text-danger fs-5" />
            Continue with Google
          </button>

          {/* Luxury Minimalist Checkbox Wrapper */}
          <label
            className="d-flex align-items-start gap-3 p-3 rounded-4 mb-4 text-start user-select-none interaction-card shadow-sm"
            htmlFor="acceptTerms"
            style={{
              // Combines full card-bg with a subtle tint behavior that works everywhere
              background: "var(--card-bg, #1e293b)",
              // Uses the theme's dynamic border color with fallback
              border: "1px solid var(--border-color, rgba(0, 0, 0, 0.1))",
              cursor: "pointer",
              transition: "transform 0.2s ease, border-color 0.2s ease"
            }}
            // Quick premium hover effect
            onMouseOver={(e) => e.currentTarget.style.borderColor = themeColor}
            onMouseOut={(e) => e.currentTarget.style.borderColor = "var(--border-color)"}
          >
            <div className="position-relative d-flex align-items-center mt-1">
              <input
                className="form-check-input m-0 custom-premium-checkbox"
                type="checkbox"
                id="acceptTerms"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                style={{
                  width: "18px",
                  height: "18px",
                  cursor: "pointer",
                  accentColor: themeColor,
                  // Dynamic border that scales visibility based on theme variables
                  border: "1px solid var(--border-color, rgba(0, 0, 0, 0.25))"
                }}
              />
            </div>
            {/* Swapped text-muted for var(--text-color) at 0.85 opacity so it has high contrast on light backgrounds */}
            <span className="small" style={{ fontSize: "0.8rem", lineHeight: "1.4", opacity: 0.85, color: "var(--text-color, inherit)" }}>
              I accept the court rules, privacy policies, and allow my public rating to be shown on the leaderboard.
            </span>
          </label>

          { }
          <div className="d-flex align-items-center justify-content-center gap-3 mt-2 mb-1">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none legal-link"
              style={{ fontSize: "0.75rem", color: "var(--text-color)", opacity: 0.5, fontWeight: "500" }}
              onClick={() => setShowTerms(true)}
            >
              Privacy Policy
            </button>
            <span className="opacity-20 text-muted" style={{ fontSize: "0.7rem" }}>•</span>
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none legal-link"
              style={{ fontSize: "0.75rem", color: "var(--text-color)", opacity: 0.5, fontWeight: "500" }}
              onClick={() => setShowTerms(true)}
            >
              Terms of Service
            </button>
          </div>
        </div>
      </div>

      {/* Cinematic Smooth Fullscreen Loader Overlay */}
      {loggingIn && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center"
          style={{
            zIndex: 9999,
            backdropFilter: "blur(16px)",
            background: "rgba(15, 23, 42, 0.85)"
          }}
        >
          <div
            className="mb-4 d-flex align-items-center justify-content-center"
            style={{
              width: "64px",
              height: "64px",
              borderRadius: "22px",
              backgroundColor: themeColor,
              animation: "court-bounce 0.5s infinite alternate cubic-bezier(0.45, 0, 0.55, 1)",
              color: "#fff",
              boxShadow: `0 20px 40px ${themeColor}40`
            }}
          >
            <i className="bi bi-grid-3x3-gap-fill" style={{ fontSize: "1.5rem" }}></i>
          </div>
          <div
            className="fw-bold tracking-widest small text-uppercase"
            style={{ color: "#f8fafc", letterSpacing: "0.2em", fontSize: '0.75rem', opacity: 0.8, animation: "pulse 1.5s infinite" }}
          >
            Stepping onto the court...
          </div>
        </div>
      )}

      {/* Clean Premium Legal Modal Architecture */}
      {showTerms && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(15, 23, 42, 0.7)", backdropFilter: "blur(8px)", zIndex: 1050 }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable px-3" style={{ maxWidth: '400px' }}>
            <div
              className="modal-content border-0"
              style={{
                background: "var(--card-bg, #1e293b)",
                color: "var(--text-color, #f8fafc)",
                borderRadius: "24px",
                boxShadow: "0 30px 60px rgba(0,0,0,0.4)"
              }}
            >
              <div className="modal-header border-0 pt-4 px-4 pb-2 d-flex justify-content-between align-items-center">
                <h5 className="modal-title fw-bold d-flex align-items-center gap-2" style={{ fontSize: "1.1rem" }}>
                  <i className="bi bi-shield-check" style={{ color: themeColor }}></i>
                  Club Rules
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white opacity-50"
                  onClick={() => setShowTerms(false)}
                />
              </div>

              <div className="modal-body text-start pt-2 px-4 pb-4" style={{ fontSize: "0.85rem", opacity: 0.8, lineHeight: '1.6' }}>
                <p>By stepping into the <strong>RRC Pickleball</strong> platform, you agree to play fair and adhere to our court network rules:</p>
                <ul className="ps-3 d-flex flex-column gap-2 my-3 list-unstyled">
                  <li><i className="bi bi-check2-circle me-2" style={{ color: themeColor }}></i><strong>Authentication:</strong> Secure sign-in processed directly via Google.</li>
                  <li><i className="bi bi-check2-circle me-2" style={{ color: themeColor }}></i><strong>Visibility:</strong> Active match history profiles are open to standard members.</li>
                  <li><i className="bi bi-check2-circle me-2" style={{ color: themeColor }}></i><strong>Data Protection:</strong> Matches & ratings are safely processed. No outside advertising selling.</li>
                </ul>
                <div className="d-flex flex-column gap-2 mt-4 pt-3 border-top border-secondary border-opacity-10">
                  <a href="/terms-of-service.html" target="_blank" rel="noreferrer" className="text-decoration-none d-flex justify-content-between align-items-center py-1" style={{ color: themeColor, fontWeight: 600 }}>
                    <span>Full Terms of Service</span> <i className="bi bi-arrow-up-right small"></i>
                  </a>
                  <a href="/privacy-policy.html" target="_blank" rel="noreferrer" className="text-decoration-none d-flex justify-content-between align-items-center py-1" style={{ color: themeColor, fontWeight: 600 }}>
                    <span>Full Privacy Policy</span> <i className="bi bi-arrow-up-right small"></i>
                  </a>
                </div>
              </div>

              <div className="modal-footer border-0 pb-4 px-4 pt-0">
                <button
                  className="btn w-100 py-2.5 fw-bold"
                  style={{ backgroundColor: "rgba(255,255,255,0.06)", color: "var(--text-color, #f8fafc)", borderRadius: "12px", border: '1px solid rgba(255,255,255,0.04)' }}
                  onClick={() => setShowTerms(false)}
                >
                  Acknowledge
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <GlobalAdsModal
        clubId={CLUB_ID}
        themeColor={THEME_COLOR}
        sessionKey={`login-page`}
      />
      {/* Embedded Modern Premium Stylesheet Scopes */}
      <style>{`
        .premium-auth-btn:hover:not(:disabled) {
          background-color: var(--card-bg, #1e293b) !important;
          border-color: ${themeColor}60 !important;
          transform: translateY(-2px);
          box-shadow: 0 12px 30px ${themeColor}15 !important;
        }
        .premium-auth-btn:active:not(:disabled) {
          transform: translateY(1px);
        }
        .legal-link:hover {
          opacity: 0.9 !important;
          color: ${themeColor} !important;
        }
        @keyframes court-bounce {
          0% { transform: translateY(0) scale(1); }
          100% { transform: translateY(-20px) scale(0.95); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}